<script setup>
import { computed, ref, watch } from "vue";
import { apiUrl } from "@/vars";
import apiClient from "@/apiClient";

const props = defineProps({
  quoteId: { type: Number, required: true },
  files: { type: Array, required: false, default: () => [] },
  editable: { type: Boolean, required: false, default: false },
  compact: { type: Boolean, required: false, default: false },
});

const emit = defineEmits(["update:files"]);

const localFiles = ref([]);
const uploading = ref(false);
const deletingId = ref(null);
const error = ref(null);
const drawerOpen = ref(false);

watch(
  () => props.files,
  (value) => {
    localFiles.value = Array.isArray(value) ? [...value] : [];
  },
  { immediate: true, deep: true },
);

const sortedFiles = computed(() =>
  [...localFiles.value].sort((a, b) => Number(b?.id ?? 0) - Number(a?.id ?? 0)),
);
const previewFiles = computed(() => sortedFiles.value.slice(0, props.compact ? 3 : 5));
const remainingFileCount = computed(() => Math.max(0, sortedFiles.value.length - previewFiles.value.length));

const formatDate = (value) => {
  if (!value) return "Unknown";

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;

  return parsed.toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const onFileChange = async (event) => {
  const files = Array.from(event.target.files ?? []);
  event.target.value = "";

  if (!files.length) return;

  uploading.value = true;
  error.value = null;

  const formData = new FormData();
  files.forEach((file, index) => {
    formData.append(`files[${index}]`, file);
  });

  try {
    const response = await apiClient.post(`/api/quote/${props.quoteId}/files`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    if (!response) {
      throw new Error("Upload request did not complete.");
    }
    const uploaded = response.data ?? [];
    const nextFiles = [...uploaded, ...localFiles.value];
    localFiles.value = nextFiles;
    emit("update:files", nextFiles);
  } catch (err) {
    console.error(err);
    error.value = err.response?.data?.message ?? "Unable to upload files.";
  } finally {
    uploading.value = false;
  }
};

const removeFile = async (file) => {
  const fileId = Number(file?.id ?? 0);
  if (!fileId) return;

  deletingId.value = fileId;
  error.value = null;

  try {
    const response = await apiClient.delete(`/api/quote/${props.quoteId}/files/${fileId}`);
    if (!response) {
      throw new Error("Delete request did not complete.");
    }
    const nextFiles = localFiles.value.filter((entry) => Number(entry?.id) !== fileId);
    localFiles.value = nextFiles;
    emit("update:files", nextFiles);
  } catch (err) {
    console.error(err);
    error.value = err.response?.data?.message ?? "Unable to delete file.";
  } finally {
    deletingId.value = null;
  }
};

const openDrawer = () => {
  drawerOpen.value = true;
};

const closeDrawer = () => {
  drawerOpen.value = false;
};
</script>

<template>
  <FormBox header="Files">
    <div class="space-y-4">
      <div class="flex items-center justify-between gap-3">
        <div class="min-w-0">
          <p class="text-xs uppercase tracking-[0.18em] text-slate-400">
            Attachment rail
          </p>
          <p class="text-sm text-slate-500">
            {{ sortedFiles.length }} file{{ sortedFiles.length === 1 ? "" : "s" }}
          </p>
        </div>
        <button
          v-if="sortedFiles.length || editable"
          class="btn shrink-0"
          type="button"
          @click.prevent="openDrawer"
        >
          {{ sortedFiles.length ? "Open files" : "Add file" }}
        </button>
      </div>

      <div v-if="editable" class="flex items-center gap-3">
        <label class="btn accent cursor-pointer">
          <input
            class="hidden"
            type="file"
            multiple
            @change="onFileChange"
          />
          {{ uploading ? "Uploading…" : "Upload" }}
        </label>
        <span class="text-xs text-slate-500">
          PDFs, Office files, spreadsheets, images, and supporting documents.
        </span>
      </div>

      <div v-if="error" class="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-600">
        {{ error }}
      </div>

      <div v-if="sortedFiles.length" class="space-y-2">
        <a
          v-for="file in previewFiles"
          :key="file.id"
          class="group flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50/80 px-3 py-3 transition hover:border-slate-300 hover:bg-white"
          :href="`${apiUrl}/files/${file.id}`"
          target="_blank"
          rel="noopener noreferrer"
        >
          <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-900 text-white">
            <span class="text-xs font-semibold uppercase">
              {{ (file.filename?.split(".").pop() || "file").slice(0, 4) }}
            </span>
          </div>
          <div class="min-w-0 flex-1">
            <div class="truncate text-sm font-medium text-slate-900">
              {{ file.filename }}
            </div>
            <div class="truncate text-xs text-slate-500">
              {{ formatDate(file.created_at) }}
            </div>
          </div>
        </a>
        <div
          v-if="remainingFileCount > 0"
          class="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-3 py-2 text-xs text-slate-500"
        >
          +{{ remainingFileCount }} more file{{ remainingFileCount === 1 ? "" : "s" }}
        </div>
      </div>

      <div v-else class="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-3 py-4 text-sm text-slate-500">
        No files uploaded yet. Add briefs, PDFs, spreadsheets, or quotes here.
      </div>
    </div>

    <div
      v-if="drawerOpen"
      class="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 px-4 py-6 backdrop-blur"
      @click.self="closeDrawer"
    >
      <div class="flex max-h-[88vh] w-full max-w-4xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl">
        <div class="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
          <div>
            <p class="text-xs uppercase tracking-[0.18em] text-slate-400">
              Quote attachments
            </p>
            <h3 class="text-lg font-semibold text-slate-900">Files and supporting documents</h3>
          </div>
          <button class="btn" type="button" @click.prevent="closeDrawer">
            Close
          </button>
        </div>

        <div class="flex-1 overflow-y-auto p-5">
          <div class="space-y-4">
            <div v-if="editable" class="flex flex-wrap items-center gap-3">
              <label class="btn accent cursor-pointer">
                <input
                  class="hidden"
                  type="file"
                  multiple
                  @change="onFileChange"
                />
                {{ uploading ? "Uploading…" : "Upload files" }}
              </label>
              <span class="text-sm text-slate-500">
                Add supporting PDFs, docs, spreadsheets, and images.
              </span>
            </div>

            <div v-if="error" class="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-600">
              {{ error }}
            </div>

            <div v-if="sortedFiles.length" class="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              <div
                v-for="file in sortedFiles"
                :key="file.id"
                class="group rounded-2xl border border-slate-200 bg-slate-50/80 p-4 transition hover:border-slate-300 hover:bg-white"
              >
                <a
                  class="block"
                  :href="`${apiUrl}/files/${file.id}`"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <div class="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-900 text-white">
                    <span class="text-xs font-semibold uppercase">
                      {{ (file.filename?.split(".").pop() || "file").slice(0, 4) }}
                    </span>
                  </div>
                  <div class="truncate text-sm font-medium text-slate-900">
                    {{ file.filename }}
                  </div>
                  <div class="mt-1 text-xs text-slate-500">
                    {{ formatDate(file.created_at) }}
                    <span v-if="file.user?.name">• {{ file.user.name }}</span>
                  </div>
                </a>
                <div v-if="editable" class="mt-3">
                  <button
                    class="btn w-full"
                    type="button"
                    :disabled="deletingId === file.id"
                    @click.prevent="removeFile(file)"
                  >
                    {{ deletingId === file.id ? "Deleting…" : "Delete" }}
                  </button>
                </div>
              </div>
            </div>

            <div v-else class="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
              No files uploaded yet.
            </div>
          </div>
        </div>
      </div>
    </div>
  </FormBox>
</template>
